import React, { useState, useContext, useEffect } from 'react';
import { WalletConnectContext } from '../contexts/WalletConnectContext';
import { ContractService } from '../services/contractService';
import { hashPackWallet } from '../services/wallets/hashpack/hashpackWallet';

interface LotteryInfo {
  lotteryId: number;
  startTime: number;
  endTime: number;
  prizePool: string;
  participantCount: number;
  winner: string;
  winningNumber: number;
  isActive: boolean;
  isEnded: boolean;
}

interface UserLotteryStats {
  totalWinnings: string;
  participationCount: number;
}

export const SwapLottery: React.FC = () => {
  const { accountId } = useContext(WalletConnectContext);

  const [currentLotteryId, setCurrentLotteryId] = useState<number>(0);
  const [lotteryInfo, setLotteryInfo] = useState<LotteryInfo | null>(null);
  const [userStats, setUserStats] = useState<UserLotteryStats | null>(null);
  const [userTickets, setUserTickets] = useState<number[]>([]);
  const [participants, setParticipants] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [winningNumber, setWinningNumber] = useState<string>('');
  const [prizePoolAmount, setPrizePoolAmount] = useState<string>('');

  const contractService = accountId ? new ContractService(hashPackWallet) : null;
  const isConnected = !!accountId;
  const connectedAddress = accountId;

  useEffect(() => {
    if (contractService) {
      fetchCurrentLottery();
    }
  }, [contractService]);

  useEffect(() => {
    if (contractService && connectedAddress && currentLotteryId > 0) {
      fetchUserLotteryData();
    }
  }, [contractService, connectedAddress, currentLotteryId]);

  const fetchCurrentLottery = async () => {
    if (!contractService) return;

    try {
      setLoading(true);
      // For now, let's set a default current lottery ID
      // In a real implementation, you'd parse the transaction result
      const lotteryId = 1; // Default to lottery 1
      setCurrentLotteryId(lotteryId);

      // Note: Contract calls through Hedera SDK return transaction IDs, not data
      // For read operations, you'd need to use a mirror node or different approach
      console.log('Contract service initialized, using default lottery ID:', lotteryId);
    } catch (error) {
      console.error('Error fetching current lottery:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserLotteryData = async () => {
    if (!contractService || !connectedAddress) return;

    try {
      // For now, set mock data since contract read operations return transaction IDs
      setUserStats({
        totalWinnings: '0',
        participationCount: 0
      });

      setUserTickets([]);
      console.log('Mock user lottery data set');
    } catch (error) {
      console.error('Error fetching user lottery data:', error);
    }
  };

  const handleClaimPrize = async () => {
    if (!contractService || !lotteryInfo) return;

    setLoading(true);
    try {
      const result = await contractService.claimPrize(lotteryInfo.lotteryId);
      console.log('Prize claimed:', result);
      await fetchCurrentLottery(); // Refresh data
      await fetchUserLotteryData();
    } catch (error) {
      console.error('Error claiming prize:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectWinner = async () => {
    if (!contractService || !lotteryInfo || !winningNumber) return;

    setLoading(true);
    try {
      const result = await contractService.selectWinner(
        lotteryInfo.lotteryId,
        parseInt(winningNumber)
      );
      console.log('Winner selected:', result);
      await fetchCurrentLottery(); // Refresh data
    } catch (error) {
      console.error('Error selecting winner:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToPrizePool = async () => {
    if (!contractService || !prizePoolAmount) return;

    setLoading(true);
    try {
      const result = await contractService.addToPrizePool(parseFloat(prizePoolAmount));
      console.log('Added to prize pool:', result);
      await fetchCurrentLottery(); // Refresh data
      setPrizePoolAmount('');
    } catch (error) {
      console.error('Error adding to prize pool:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeRemaining = (endTime: number) => {
    const now = Math.floor(Date.now() / 1000);
    const remaining = endTime - now;

    if (remaining <= 0) return 'Ended';

    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    return `${hours}h ${minutes}m remaining`;
  };

  const formatHBAR = (amount: string) => {
    if (!amount || amount === '0') return '0';
    return (parseInt(amount) / 1e18).toFixed(4);
  };

  if (!isConnected) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-4">Swap Lottery</h2>
        <p className="text-gray-600">Please connect your wallet to view lottery information.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Lottery Status */}
      <div className="p-6 bg-white rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-6">🎲 Swap Lottery</h2>

        {loading && <p className="text-center text-gray-600">Loading...</p>}

        {lotteryInfo && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Lottery #{lotteryInfo.lotteryId}</p>
                <p className="text-2xl font-bold text-blue-600">
                  {lotteryInfo.isActive ? '🔴 ACTIVE' : lotteryInfo.isEnded ? '✅ ENDED' : '⏳ PENDING'}
                </p>
              </div>

              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600">Prize Pool</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatHBAR(lotteryInfo.prizePool)} HBAR
                </p>
              </div>

              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-600">Participants</p>
                <p className="text-2xl font-bold text-purple-600">{lotteryInfo.participantCount}</p>
              </div>
            </div>

            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-gray-600">Time Status</p>
              <p className="text-lg font-bold text-yellow-700">
                {formatTimeRemaining(lotteryInfo.endTime)}
              </p>
            </div>

            {/* Winner Information */}
            {lotteryInfo.isEnded && lotteryInfo.winner && lotteryInfo.winner !== '0x0000000000000000000000000000000000000000' && (
              <div className="p-4 bg-gold-50 border-2 border-yellow-400 rounded-lg">
                <h4 className="font-bold text-yellow-800 mb-2">🏆 Winner Selected!</h4>
                <p className="text-sm"><strong>Winner:</strong> {lotteryInfo.winner}</p>
                <p className="text-sm"><strong>Winning Number:</strong> {lotteryInfo.winningNumber}</p>

                {lotteryInfo.winner.toLowerCase() === connectedAddress?.toLowerCase() && (
                  <button
                    onClick={handleClaimPrize}
                    disabled={loading}
                    className="mt-2 bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 disabled:opacity-50"
                  >
                    {loading ? 'Claiming...' : 'Claim Prize! 🎉'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Stats & Tickets */}
      {userStats && (
        <div className="p-6 bg-white rounded-lg shadow-lg">
          <h3 className="text-xl font-bold mb-4">Your Lottery Stats</h3>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-4 bg-indigo-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Winnings</p>
              <p className="text-xl font-bold text-indigo-600">
                {formatHBAR(userStats.totalWinnings)} HBAR
              </p>
            </div>

            <div className="p-4 bg-pink-50 rounded-lg">
              <p className="text-sm text-gray-600">Lotteries Participated</p>
              <p className="text-xl font-bold text-pink-600">{userStats.participationCount}</p>
            </div>
          </div>

          {/* User Tickets for Current Lottery */}
          {userTickets.length > 0 && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-2">Your Tickets for Lottery #{currentLotteryId}</h4>
              <div className="flex flex-wrap gap-2">
                {userTickets.map((ticket, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-mono"
                  >
                    #{ticket}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Admin Functions */}
      <div className="p-6 bg-white rounded-lg shadow-lg">
        <h3 className="text-xl font-bold mb-4">Lottery Actions</h3>

        <div className="space-y-4">
          {/* Add to Prize Pool */}
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Amount in HBAR"
              className="flex-1 p-2 border border-gray-300 rounded"
              value={prizePoolAmount}
              onChange={(e) => setPrizePoolAmount(e.target.value)}
            />
            <button
              onClick={handleAddToPrizePool}
              disabled={loading || !prizePoolAmount}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
            >
              Add to Prize Pool
            </button>
          </div>

          {/* Select Winner (Admin) */}
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Winning number (1-1000000)"
              className="flex-1 p-2 border border-gray-300 rounded"
              value={winningNumber}
              onChange={(e) => setWinningNumber(e.target.value)}
            />
            <button
              onClick={handleSelectWinner}
              disabled={loading || !winningNumber || !lotteryInfo || lotteryInfo.isEnded}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
            >
              Select Winner
            </button>
          </div>
        </div>
      </div>

      {/* Participants List */}
      {participants.length > 0 && (
        <div className="p-6 bg-white rounded-lg shadow-lg">
          <h3 className="text-xl font-bold mb-4">Current Participants ({participants.length})</h3>
          <div className="max-h-40 overflow-y-auto">
            <div className="space-y-1">
              {participants.map((participant, index) => (
                <div
                  key={index}
                  className={`p-2 rounded text-sm font-mono ${
                    participant.toLowerCase() === connectedAddress?.toLowerCase()
                      ? 'bg-blue-100 text-blue-800 font-bold'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {participant.toLowerCase() === connectedAddress?.toLowerCase() && '👤 '}
                  {participant}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};